import React, { useEffect, useRef, useState } from 'react';
import { select, line, curveBasis, area, scaleBand, scalePoint, scaleOrdinal,
    scaleLinear, max, min, axisBottom, extent, ticks, scaleThreshold, transition, easeSin } from "d3";
import _ from "lodash";
import { useTranslation } from 'react-i18next';

function MovingChart({data, count, avgData, type}) {
    let innerData = data.slice(-count);
    const svgRef = useRef(null);
    const { t } = useTranslation();
    let useLang = localStorage.getItem('language');

    useEffect(() => {
        let statParam = {
            position: type,
            movingAvgSize: count,
            movingAvgMin: 0,
            movingAvgMax: 1
        }

        let movingData = statCalc(data, statParam);
        let sortData = _.sortBy(movingData, 'gameDay').slice(-count);

        const svg = select(svgRef.current);
        let svgDom = svg
            .append('g')
            .attr('transform', 'translate(10,10)');

        svgDom.append("text")
            .attr("x", 60)
            .attr("y", 150)
            .text(t('last')+" "+count+t('pa'));

        if(data.length !== 0) {
            let statCalcData = (type === 'batter') ? avgData['statCacled'] : avgData['statCalced'];

            let dataLength = innerData.length;
            let tickLength = ticks(0, dataLength, 5);
            
            // let maxValue = Math.ceil(_.maxBy(movingData, (item) => item['ops']['original'])['ops']['original']);
            let maxValue = statCalcData['ops']*2;
            let minValue = 0;

            const xScale = scaleLinear()
            .domain([0, dataLength])
            .range([0, 200]); //width 200

            const yScale = scaleLinear()
            .domain([minValue, maxValue])
            .range([100, 0]); //height 100

            let colorScale = scaleThreshold(
                [minValue, statCalcData['ops'], maxValue], ["gray", "red", 'blue', 'gray']
            ).unknown("black");

            let dateScale = scalePoint()
            .domain(tickLength)
            .range([0, 200]);

            const myLine = line()
            .x((value, idx) => {
                return xScale(idx)
            })
            .y((value) => yScale(value['ops']['original']))
            .curve(curveBasis);

            svgDom.append("linearGradient")
            .attr("id", 'color')
            .attr("gradientUnits", "userSpaceOnUse")
            .attr("x1", 0)
            .attr("x2", 0)
            .attr("y1", 0)
            .attr("y2", 100)
            .selectAll("stop")
            .data(
                type === 'batter' ?
                [
                {offset: yScale(statCalcData['ops']) / 100, color: "red"},
                {offset: yScale(statCalcData['ops']) / 100, color: "blue"}
                ] :
                [
                {offset: yScale(statCalcData['ops']) / 100, color: "blue"},
                {offset: yScale(statCalcData['ops']) / 100, color: "red"}
                ]
            )
            .join("stop")
            .attr("offset", d => d.offset)
            .attr("stop-color", d => d.color);

            let dataDom = svgDom
            .append('path')
            .data([sortData])
            
            dataDom
            .join((enter) => enter.append("path"))
            .attr("d", (value, idx) => {
                return myLine(value);
            })
            .attr("stroke", 'url(#color)')
            .attr("fill", 'none');

            const pathLength = dataDom.node().getTotalLength();            
            const transitionPath = transition().ease(easeSin).duration(1000);

            dataDom
            .attr("stroke-dashoffset", pathLength)
            .attr("stroke-dasharray", pathLength)
            .transition(transitionPath)
            .attr("stroke-dashoffset", 0);

            svgDom.append('line')
            .attr("x1", 0)
            .attr("x2", 200)
            .attr("y1", yScale(statCalcData['ops']))
            .attr("y2", yScale(statCalcData['ops']))
            .attr("stroke", 'gray')
            .attr("stroke-dasharray", '4,4')
            .attr("stroke-width", 3);

            svgDom.append('line') //start line
            .attr("x1", 0)
            .attr("x2", 200)
            .attr("y1", 0)
            .attr("y2", 0)
            .attr("stroke", 'gray');

            svgDom.append("text") // league ops
            .attr("x", 200)
            .attr("y", yScale(statCalcData['ops']))
            .attr("alignment-baseline", "middle")
            .attr("font-size", "12px")
            .text(statCalcData['ops']);

            svgDom.append("text")
            .attr("x", useLang ==="en" ? 160 : 180)
            .attr("y", yScale(statCalcData['ops'])-15)
            .attr("alignment-baseline", "middle")
            .attr("font-size", "12px")
            .text(t('leagueOps'));

            let dateAxis = axisBottom()
            .scale(dateScale)
            // .ticks(5)
            // .tickFormat((d) => {
            //     let newDate = new Date(d);
            //     return timeFormat('%m.%d')(newDate);
            // });

            svgDom.append("g") // grid
            .attr("class", "axis")
            .attr("transform", "translate(0,100)")
            .call(dateAxis)
            // .selectAll("text")
            // .style("text-anchor", "middle")
            // .attr("dx", "-.8em")
            // .attr("dy", ".15em")
            // .attr("transform", "translate(9,5)")
            // .filter((d, i) => {
            //     if(tickLength.indexOf(i) > -1) {
            //         return false;
            //     } else {
            //         return true;
            //     }
            // })
            // .remove();
        } else {
            svgDom.append("text")
            .attr("x", 70)
            .attr("y", 70)
            .text("No Data");
        }
        
        return () => {
            svg.selectAll('*').remove();
        }
    }, [data]);

    function statCalc(data, param, cFip){
        // console.log(data);
        // console.log(param);

        // let statArr = param['movingAvgKey'].split(',');
        let statArr = ['ops'];
        let size = param['movingAvgSize'] * 1;
        let min = param['movingAvgMin'] * 1;
        let max = param['movingAvgMax'] * 1;

        // console.log(statArr);

        let resultArr = [];

        let totalAB = 0;
        let totalHIT = 0;

        let totalBB = 0;
        let totalHBP = 0;
        let totalSF = 0;

        let total1B = 0;
        let total2B = 0;
        let total3B = 0;
        let totalHR = 0;

        let totalPA = 0;
        let totalK = 0;

        let totalER = 0;
        let totalOutCount = 0;
        let dataLength = data['length'];

        for(let i = 0; i < data.length; i++){
            let statObj = {};

            let gameDay = data[i]['gameDay'];

            if(data[i]){
                totalAB += data[i]['stat']['ab'] * 1;
                totalHIT += data[i]['stat']['hit'] * 1;

                totalBB += data[i]['stat']['bb'] * 1;
                totalHBP += data[i]['stat']['hbp'] * 1;
                totalSF += data[i]['stat']['sf'] * 1;

                total1B += data[i]['stat']['b1b'] * 1;
                total2B += data[i]['stat']['b2b'] * 1;
                total3B += data[i]['stat']['b3b'] * 1;
                totalHR += data[i]['stat']['hr'] * 1;

                totalPA += data[i]['stat']['tpa'] * 1;
                totalK += data[i]['stat']['so'] * 1;

                if(param['position'] === 'pitcher'){
                    totalER += data[i]['stat']['er'] * 1;
                    totalOutCount += data[i]['stat']['totalOutCount'] * 1;
                }
            }

            if(i >= size){
                if(data[i - size]){
                    totalAB -= data[i - size]['stat']['ab'] * 1;
                    totalHIT -= data[i - size]['stat']['hit'] * 1;

                    totalBB -= data[i - size]['stat']['bb'] * 1;
                    totalHBP -= data[i - size]['stat']['hbp'] * 1;
                    totalSF -= data[i - size]['stat']['sf'] * 1;

                    total1B -= data[i - size]['stat']['b1b'] * 1;
                    total2B -= data[i - size]['stat']['b2b'] * 1;
                    total3B -= data[i - size]['stat']['b3b'] * 1;
                    totalHR -= data[i - size]['stat']['hr'] * 1;

                    totalPA -= data[i - size]['stat']['tpa'] * 1;
                    totalK -= data[i - size]['stat']['so'] * 1;

                    if(param['position'] === 'pitcher'){
                        totalER -= data[i - size]['stat']['er'] * 1;
                        totalOutCount -= data[i - size]['stat']['totalOutCount'] * 1;
                    }
                }
            }

            // console.log(totalAB);
            // console.log(totalHIT);

            // console.log(totalBB);
            // console.log(totalHBP);
            // console.log(totalSF);

            // console.log(total1B);
            // console.log(total2B);
            // console.log(total3B);
            // console.log(totalHR);

            // console.log(totalPA);
            // console.log(totalK);

            // console.log(totalER);
            // console.log(totalOutCount);

            if(statArr.indexOf('avg') > -1){
                let avg = totalHIT / totalAB;

                let rounded = Math.round((avg + Number.EPSILON) * 1000) / 1000;
                let origalValue = rounded;

                // console.log(rounded);

                if(rounded < min){
                    rounded = min;
                }

                if(rounded > max){
                    rounded = max;
                }

                statObj.avg = {
                    limited: rounded,
                    original: origalValue
                }
            }

            if(statArr.indexOf('obp') > -1){
                let obp = (totalHIT + totalBB + totalHBP) / (totalAB + totalBB + totalHBP + totalSF);

                let rounded = Math.round((obp + Number.EPSILON) * 1000) / 1000;
                let origalValue = rounded;

                // console.log(rounded);

                if(rounded < min){
                    rounded = min;
                }

                if(rounded > max){
                    rounded = max;
                }

                statObj.obp = {
                    limited: rounded,
                    original: origalValue
                }
            }

            if(statArr.indexOf('slg') > -1){
                let slg = (total1B + total2B*2 + total3B*3 + totalHR*4) / totalAB;

                let rounded = Math.round((slg + Number.EPSILON) * 1000) / 1000;
                let origalValue = rounded;

                // console.log(rounded);

                if(rounded < min){
                    rounded = min;
                }

                if(rounded > max){
                    rounded = max;
                }

                statObj.slg = {
                    limited: rounded,
                    original: origalValue
                }
            }

            if(statArr.indexOf('ops') > -1){
                let obp = ((totalHIT + totalBB + totalHBP) / (totalAB + totalBB + totalHBP + totalSF)) || 0;
                let slg = ((total1B + total2B*2 + total3B*3 + totalHR*4) / totalAB ) || 0;

                let ops = obp + slg;

                let rounded = Math.round((ops + Number.EPSILON) * 1000) / 1000;
                let origalValue = rounded;

                // console.log(rounded);

                if(rounded < min){
                    rounded = min;
                }

                if(rounded > max){
                    rounded = max;
                }

                statObj.ops = {
                    limited: rounded,
                    original: origalValue
                }
            }

            if(statArr.indexOf('bbRate') > -1){
                let bbRate = totalBB / totalPA * 100;

                let rounded = Math.round((bbRate + Number.EPSILON) * 100) / 100;
                let origalValue = rounded;

                // console.log(rounded);

                if(rounded < min){
                    rounded = min;
                }

                if(rounded > max){
                    rounded = max;
                }

                statObj.bbRate = {
                    limited: rounded,
                    original: origalValue
                }
            }

            if(statArr.indexOf('kRate') > -1){
                let kRate = totalK / totalPA * 100;

                let rounded = Math.round((kRate + Number.EPSILON) * 100) / 100;
                let origalValue = rounded;

                // console.log(rounded);

                if(rounded < min){
                    rounded = min;
                }

                if(rounded > max){
                    rounded = max;
                }

                statObj.kRate = {
                    limited: rounded,
                    original: origalValue
                }
            }

            if(statArr.indexOf('era') > -1){
                if(param['position'] === 'pitcher'){
                    let era = totalER * 27 / totalOutCount;

                    let rounded = Math.round((era + Number.EPSILON) * 100) / 100;
                    let origalValue = rounded;

                    // console.log(rounded);

                    if(rounded < min){
                        rounded = min;
                    }

                    if(rounded > max){
                        rounded = max;
                    }

                    statObj.era = {
                        limited: rounded,
                        original: origalValue
                    }
                } else {
                    statObj.era = {
                        limited: 0,
                        original: 0
                    }
                }
            }

            if(statArr.indexOf('fip') > -1){
                if(param['position'] === 'pitcher'){
                    let fip = ((13 * totalHR) + (3 * (totalBB + totalHBP)) - (2 * totalK)) / (totalOutCount / 3) + cFip;

                    let rounded = Math.round((fip + Number.EPSILON) * 100) / 100;
                    let origalValue = rounded;

                    // console.log(rounded);

                    if(rounded < min){
                        rounded = min;
                    }

                    if(rounded > max){
                        rounded = max;
                    }

                    statObj.fip = {
                        limited: rounded,
                        original: origalValue
                    }
                } else {
                    statObj.fip = {
                        limited: 0,
                        original: 0
                    }
                }
            }

            if(statArr.indexOf('whip') > -1){
                if(param['position'] === 'pitcher'){
                    let whip = (totalBB + totalHIT) / (totalOutCount / 3);

                    let rounded = Math.round((whip + Number.EPSILON) * 100) / 100;
                    let origalValue = rounded;

                    // console.log(rounded);

                    if(rounded < min){
                        rounded = min;
                    }

                    if(rounded > max){
                        rounded = max;
                    }

                    statObj.whip = {
                        limited: rounded,
                        original: origalValue
                    }
                } else {
                    statObj.whip = {
                        limited: 0,
                        original: 0
                    }
                }
            }

            statObj.gameDay = gameDay;
            statObj['tpa'] = dataLength-i;

            resultArr.push(statObj);
        }

        return resultArr;
    }

    return (
        <div style={{textAlign: 'center'}}>
            <svg width="240" height="170" ref={svgRef}>
                
            </svg>
        </div>
    )
}

export default MovingChart;